import styled from "@emotion/styled";
import {
  Box,
  Typography,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import { colors } from "../../theme/colors";

export const PageContainer = styled(Box)`
  display: flex;
  flex-direction: column;
  height: 100%;
  gap: 24px;
`;

export const PageHeaderContainer = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

export const TitleRow = styled(Box)`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
`;

export const HeaderActions = styled(Box)`
  display: flex;
  align-items: center;
  gap: 16px;
  position: relative;
`;

export const SearchContainer = styled(Box)`
  display: flex;
  align-items: center;
  background: ${colors.background.secondary};
  border: 1px solid ${colors.border.light};
  border-radius: 8px;
  padding: 0 16px;
  height: 44px;
  width: 280px;

  input {
    border: none;
    outline: none;
    background: transparent;
    width: 100%;
    font-size: 14px;
    font-family: "Nunito", sans-serif;
    color: ${colors.text.primary};
    margin-left: 8px;

    &::placeholder {
      color: ${colors.text.secondary};
    }
  }
`;

export const ImportButton = styled(Button)`
  background-color: ${colors.amber.main};
  color: #ffffff;
  font-weight: 600;
  font-size: 14px;
  text-transform: none;
  padding: 10px 24px;
  border-radius: 8px;
  font-family: "Nunito", sans-serif;
  box-shadow: none;

  &:hover {
    background-color: ${colors.amber.dark};
    box-shadow: none;
  }
`;

export const UpdatesSavedBadge = styled(Box, {
  shouldForwardProp: (prop) => prop !== "$isError",
})<{ $isError?: boolean }>`
  position: absolute;
  top: -55px;
  right: 0;
  display: flex;
  align-items: center;
  gap: 8px;
  background-color: ${({ $isError }) => ($isError ? "#fef2f2" : "#f0fdf4")};
  border: 1px solid ${({ $isError }) => ($isError ? "#fca5a5" : "#86efac")};
  border-radius: 20px;
  padding: 8px 16px;
  color: ${({ $isError }) => ($isError ? "#dc2626" : "#16a34a")};
  font-size: 14px;
  font-weight: 600;
  font-family: "Nunito", sans-serif;
  z-index: 10;
`;

export const ContentContainer = styled(Box)`
  flex: 1;
  overflow-y: auto;
  padding-right: 8px;
`;

export const StyledAccordion = styled(Accordion)`
  background-color: ${colors.background.default};
  border: none;
  border-radius: 8px !important;
  margin-bottom: 12px;
  box-shadow: none;

  &:before {
    display: none;
  }

  &.Mui-expanded {
    margin-bottom: 12px;
  }
`;

export const StyledAccordionSummary = styled(AccordionSummary)`
  padding: 0 16px;
  min-height: 48px;
  height: 48px;
  flex-direction: row-reverse;

  &.Mui-expanded {
    min-height: 48px;
    height: 48px;
  }

  .MuiAccordionSummary-content {
    margin: 0;
    margin-left: 12px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex: 1;

    &.Mui-expanded {
      margin: 0;
      margin-left: 12px;
    }
  }

  .MuiAccordionSummary-expandIconWrapper {
    color: ${colors.text.primary};
    transform: rotate(-90deg);

    &.Mui-expanded {
      transform: rotate(0deg);
    }
  }
`;

export const AccordionTitle = styled(Typography)`
  font-size: 16px;
  font-weight: 700;
  color: ${colors.background.darkBlue};
  font-family: "Nunito", sans-serif;
  line-height: 24px;
`;

export const SearchResultsCount = styled(Typography)`
  font-size: 16px;
  font-weight: 400;
  color: ${colors.text.secondary};
  font-family: "Nunito", sans-serif;
  margin-left: 24px;
`;

export const AccordionCodeRange = styled(Typography)`
  font-size: 14px;
  color: ${colors.text.secondary};
  font-family: "Nunito", sans-serif;
`;

export const StyledAccordionDetails = styled(AccordionDetails)`
  padding: 0;
  background-color: ${colors.background.secondary};
  display: flex;
  flex-direction: column;
`;

export const AddCustomProcedureButton = styled(Button)`
  border: 1px solid ${colors.primary.main};
  color: ${colors.primary.main};
  font-weight: 700;
  font-size: 14px;
  text-transform: none;
  height: 34px;
  padding: 0 16px;
  border-radius: 8px;
  font-family: "Nunito", sans-serif;
  background-color: transparent;
  marginLeft: "auto",

  &:hover {
    background-color: ${colors.background.lightBlue};
    border-color: ${colors.primary.main};
  }
`;

export const ProcedureRow = styled(Box)`
  display: grid;
  grid-template-columns: 80px 160px 1fr 100px 100px 80px;
  gap: 16px;
  align-items: center;
  padding: 12px 16px;
  border-bottom: 1px solid ${colors.border.medium};

  &:last-of-type {
    border-bottom: none;
  }

  &.standard-procedure {
    grid-template-columns: 80px 160px 1fr 100px 100px;
  }
`;

export const ProcedureCode = styled(Typography)`
  font-size: 14px;
  font-weight: 600;
  color: ${colors.text.primary};
  font-family: "Nunito", sans-serif;
`;

export const ProcedureName = styled(Typography)`
  font-size: 14px;
  font-weight: 500;
  color: ${colors.text.primary};
  font-family: "Nunito", sans-serif;
  min-width: 0;
  word-break: break-word;
  overflow-wrap: break-word;
`;

export const ProcedureDescription = styled(Typography)`
  font-size: 14px;
  color: ${colors.text.secondary};
  font-family: "Nunito", sans-serif;
  line-height: 1.5;
  min-width: 0;
  word-break: break-word;
  overflow-wrap: break-word;
`;

export const InputContainer = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

export const InputLabel = styled(Typography)`
  font-size: 12px;
  color: ${colors.text.secondary};
  font-family: "Nunito", sans-serif;
`;

export const NumberInput = styled("input")`
  width: 100%;
  height: 40px;
  border: 1px solid ${colors.border.light};
  border-radius: 8px;
  padding: 0 12px;
  font-size: 14px;
  font-family: "Nunito", sans-serif;
  color: ${colors.text.primary};
  background-color: ${colors.background.secondary};
  outline: none;
  text-align: right;

  &:focus {
    border-color: ${colors.primary.main};
  }

  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  &[type="number"] {
    -moz-appearance: textfield;
  }
`;

export const ActionButtons = styled(Box)`
  display: flex;
  gap: 8px;
  justify-content: flex-end;
  align-items: center;
`;

export const ActionButton = styled(Button)`
  min-width: auto;
  padding: 8px;
  border-radius: 8px;
  border: none;
  background-color: transparent;

  &:hover {
    background-color: transparent;
  }

  svg {
    width: 20px;
    height: 20px;
  }
`;

export const NoResultsMessage = styled(Box)`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 48px;
  color: ${colors.text.secondary};
  font-size: 16px;
  font-family: "Nunito", sans-serif;
`;

export const CustomProcedureHeader = styled(Box)`
  display: flex;
  align-items: center;
`;
